import {useState} from 'react';
import {sealObservation,reconcileObservation} from './review-reconcile';
import {evidenceSource,reviewPacket,type ReviewRecord,type Observation} from './visual-evidence';
import '../../styles/g16-visual-review.css';
import '../../styles/g17-evidence.css';
const label:Record<Observation,string>={unreviewed:'Nicht geprüft',observed:'Screenshot gesichtet',defect:'Problem entdeckt'};
const kind=(file:string)=>file.includes('host-200pct')?'Host bei 200 % Zoom':
 file.includes('revealed-1280x720')?'Lösung bei 1280 × 720':'Frage bei 1280 × 720';
export function VisualEvidenceReview(){
 const [reviews,setReviews]=useState<Record<string,ReviewRecord>>({});
 const [notes,setNotes]=useState('');
 const [expanded,setExpanded]=useState(false);
 const [reviewStatus,setReviewStatus]=useState('');
 const packet=reviewPacket(reviews,notes);
 function update(file:string,patch:Partial<ReviewRecord>){
  setReviewStatus('');
  setReviews(prev=>({...prev,[file]:{observation:prev[file]?.observation??'unreviewed',
   note:prev[file]?.note??'',...patch}}));
 }
 function exportPacket(){
  const blob=new Blob([JSON.stringify(reviewPacket(reviews,notes),null,2)],{type:'application/json'});
  const url=URL.createObjectURL(blob),anchor=document.createElement('a');
  try{anchor.href=url;anchor.download='ligoquiz-g16-sichtungsnotizen.json';anchor.click();}
  finally{window.setTimeout(()=>URL.revokeObjectURL(url),1000);}
 }
 async function exportReconciled(){
  try{
   const envelope=await sealObservation(reviews,notes);
   const url=URL.createObjectURL(new Blob([JSON.stringify(envelope,null,2)],{type:'application/json'}));
   const a=document.createElement('a');
   try{a.href=url;a.download='ligoquiz-g17-pruefnotizen-mit-sha256.json';a.click();}
   finally{window.setTimeout(()=>URL.revokeObjectURL(url),1000);}
   setReviewStatus('SHA-256-Selbstkonsistenz exportiert. Nicht signiert oder freigegeben.');
  }catch{setReviewStatus('Export fehlgeschlagen; keine Freigabe erteilt.');}
 }
 async function importReconciled(file:File|undefined){
  if(!file)return;
  if(file.size>65536){setReviewStatus('Datei größer als 64 KiB; Annahme verweigert.');return;}
  try{
   const report=await reconcileObservation(JSON.parse(await file.text()) as unknown);
   setReviews(Object.fromEntries(report.records.map(r=>[r.file,{observation:r.observation,note:r.note}])));
   setNotes(report.notes);
   setReviewStatus('15 gepinnte Bildnachweise und Prüfsumme konsistent. Nur Sichtung, keine Freigabe.');
  }catch(e){setReviewStatus('Prüfung abgelehnt: '+(e instanceof Error?e.message:'ungültige JSON-Daten'));}
 }
  return <section className="g16-review" aria-label="Visuelle Evidenzprüfung">
  
  <h2>Visuelle Evidenz prüfen</h2>
  <p>15 unveränderte synthetische Chromium-Screenshots aus
   <a href="https://github.com/thiepn/ligoquiz/actions/runs/38040891245" target="_blank"
    rel="noopener noreferrer"> G15 CI</a>. Quelle {evidenceSource.head.slice(0,12)},
    Artefakt #{evidenceSource.artifactId}; jede Datei hat eine gepinnte SHA-256-Prüfsumme.</p>
  <p className="g16-review-denied">KEINE FREIGABE: Sichtungsnotizen sind weder eine physische Geräteprüfung
   noch eine redaktionelle, barrierefreie oder produktive Abnahme.</p>
  <div className="g16-review-summary" aria-live="polite">
   <strong>{packet.summary.observed} von 15 gesichtet</strong>
   <strong>{packet.summary.defects} Probleme</strong>
   <strong>{packet.summary.unreviewed} ungeprüft</strong>
  </div>
  <button type="button" aria-expanded={expanded} className="g16-review-toggle"
   onClick={()=>setExpanded(v=>!v)}>{expanded?'Bildliste schließen':'15 Bildnachweise sichten'}</button>
  {expanded&&<div className="g16-review-list">
   {evidenceSource.files.map(item=><div className="g16-review-item" key={item.file}>
    <div><strong>{item.game} · {kind(item.file)}</strong><small>{item.file}</small>
      <code title="SHA-256">{item.sha256}</code></div>
    <label>Status
     <select value={reviews[item.file]?.observation??'unreviewed'}
       onChange={e=>update(item.file,{observation:e.target.value as Observation})}>
      {(['unreviewed','observed','defect'] as const).map(o=>
        <option value={o} key={o}>{label[o]}</option>)}
     </select>
    </label>
    <label>Beobachtung
     <input value={reviews[item.file]?.note??''} maxLength={1000}
      onChange={e=>update(item.file,{note:e.target.value})}
      placeholder="Echte Beobachtung eintragen"/>
    </label>
   </div>)}
  </div>}
  <label className="g16-review-notes">Zusätzliche Sichtungsnotizen
   <textarea rows={3} maxLength={1200} value={notes} onChange={e=>{setReviewStatus('');setNotes(e.target.value);}}/>
  </label>
  <button type="button" onClick={exportPacket} className="g16-review-toggle">Notizen lokal als JSON exportieren</button>
  <div className="g17-packet">
   <h3>Quellengebundene Sichtungsnotizen abgleichen</h3>
   <p>Lokale SHA-256-Selbstkonsistenz und 15 gepinnte Bildhashes prüfen.
    Keine Signatur, unabhängige Identität oder physische Abnahme.</p>
   <button type="button" onClick={()=>void exportReconciled()}>G17-Prüfumschlag mit SHA-256 exportieren</button>
   <label>G17-Prüfumschlag importieren
    <input type="file" accept=".json,application/json" onChange={e=>{
     const file=e.currentTarget.files?.[0];e.currentTarget.value='';void importReconciled(file);
    }}/>
   </label>
   {reviewStatus&&<p role="status">{reviewStatus}</p>}
  </div>
  <p className="g16-review-denied" role="status">OFFEN: Veranstaltungsbeamer und Leseabstand,
   physische Mobilgeräte, Screenreader, redaktionelle Rechte, unabhängige menschliche Abnahme
   sowie Merge- und Deploy-Autorisierung. Kein Status dieser Oberfläche erteilt eine Freigabe.</p>
 </section>;
}
